import {
  useHPTARGeneralMessage,
  useHPTARInvalids,
  useHPTARs,
  useInfiniteKmzInvalids,
} from "@/api/hptar";
import { FormContainer } from "@/components/form/form-container";
import { FormFooter } from "@/components/form/form-footer";
import { SubmitButton } from "@/components/ui/button";
import { H4 } from "@/components/ui/typography";
import { MISSING_ID_IN_ROUTE, ROLE } from "@/utils/constant";
import { useId } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSupportingDocumentByRfsId } from "@/api/supporting-document";
import { DocumentUploadSection } from "./document-upload-section/DocumentUploadSection";
import { StatusFeeder } from "./hptar-section/StatusFeeder";
import { DataCreationSection } from "./data-creation-section/DataCreationSection";
import {
  SubmitSupportingDocumentsFormSchema,
  submitSupportingDocumentsFormSchema,
} from "./utils/useDocumentPrepare";
import {
  isGPONValidatedByRFS,
  isHPTARValidated,
  isNNEvidenceValidatedByRFS,
  isOPMValidatedByRFS,
} from "./utils/validation";
import { useDocumentReject } from "./utils/useDocumentReject";
import { swalError } from "@/utils/lib/swal";
import useCheckingProfileValidations from "./utils/useCheckingProfileValidations";
import { useRFS } from "@/api/rfs";
import { useCurrentUser } from "@/features/auth/useCurrentUser";
import { hasCommonElement } from "@/utils/helper/hasCommonElement";

export function DocumentRejectForm() {
  const { id } = useParams();
  const { roles } = useCurrentUser();
  if (!id) {
    throw new Error(MISSING_ID_IN_ROUTE);
  }

  const { data: supportingDocument } = useSupportingDocumentByRfsId({
    rfsId: id,
  });

  const formId = useId();
  const form = useForm<SubmitSupportingDocumentsFormSchema>({
    resolver: zodResolver(submitSupportingDocumentsFormSchema),
    values: {
      statusFeederAndOlt: supportingDocument?.statusFeederAndOlt ?? "",
    },
  });

  const { data: rfs } = useRFS({ id });
  const { data: hptars } = useHPTARs({ rfsId: id });
  const { data: hptarInvalids } = useHPTARInvalids({ rfsId: id });
  const { data: generalMessages } = useHPTARGeneralMessage({
    rfsId: id,
  });
  const { data: kmzInvalids } = useInfiniteKmzInvalids({ rfsId: id });

  const { fields } = useCheckingProfileValidations({
    rfs,
  });
  const { handleReject } = useDocumentReject({ id, fields, form });

  const isViewOnly = hasCommonElement(roles, [ROLE.VENDOR_VIEW]);
  const isDocumentUploadValidated = fields
    ? fields.every(
        (field) => field.status === "Validated" || field.status === "Optional",
      )
    : false;

  const isSubmitDisabled =
    !isOPMValidatedByRFS(rfs) ||
    !isNNEvidenceValidatedByRFS(rfs) ||
    !isGPONValidatedByRFS(rfs) ||
    !isHPTARValidated({
      hptars,
      hptarInvalids,
      hptarGeneralMessages: generalMessages,
      kmzInvalids: kmzInvalids?.pages?.[0]?.content,
    }) ||
    !isDocumentUploadValidated ||
    isViewOnly;

  function handleError() {
    swalError.fire({
      title: "Validation Failed",
      text: "The supporting document files have not been updated. Please check the file then try again",
    });
  }

  return (
    <>
      <FormContainer
        id={formId}
        form={form}
        onSubmit={handleReject}
        onSubmitError={handleError}
      >
        <H4>Step 1 - Preparation & Validate HPTAR</H4>

        <DataCreationSection />

        <hr />

        <H4>Step 2 - Upload & Validate Supporting Documents</H4>

        <DocumentUploadSection fields={fields} />

        <StatusFeeder />
      </FormContainer>

      <FormFooter>
        <SubmitButton form={formId} disabled={isSubmitDisabled} />
      </FormFooter>
    </>
  );
}
